import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalArtists: 0,
    totalArtworks: 0,
    totalOrders: 0,
    totalRevenue: 0
  });
  const [users, setUsers] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { user, token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (!isAdmin || !user) {
      navigate('/');
      return;
    }
    fetchAdminData();
  }, [isAdmin, user]);

  const fetchAdminData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [artworksRes, ordersRes, statsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/artworks`),
        axios.get(`${API_URL}/api/orders`, { headers }),
        axios.get(`${API_URL}/api/admin/stats`, { headers }).catch(() => null),
        axios.get(`${API_URL}/api/admin/users`, { headers }).catch(() => null)
      ]);

      setArtworks(artworksRes.data);
      setOrders(ordersRes.data);

      if (usersRes?.data) {
        setUsers(usersRes.data);
      }

      if (statsRes?.data) {
        setStats(statsRes.data);
      } else {
        // Fallback: calculate from available data
        const totalRevenue = ordersRes.data
          .filter(o => o.paymentStatus === 'paid')
          .reduce((sum, o) => sum + (o.totalAmount || 0), 0);

        setStats({
          totalUsers: usersRes?.data?.length || 0,
          totalArtists: usersRes?.data?.filter(u => u.role === 'artist').length || 0,
          totalArtworks: artworksRes.data.length,
          totalOrders: ordersRes.data.length,
          totalRevenue
        });
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      showToast('Failed to load admin data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArtwork = async (id) => {
    if (!window.confirm('Are you sure you want to delete this artwork?')) return;

    try {
      await axios.delete(`${API_URL}/api/artworks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Artwork deleted successfully!', 'success');
      fetchAdminData();
    } catch (error) {
      showToast('Failed to delete artwork', 'error');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/orders/${orderId}/status`,
        { orderStatus: status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Order status updated!', 'success');
      fetchAdminData();
    } catch (error) {
      showToast('Failed to update order status', 'error');
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(`User role updated to ${newRole}`, 'success');
      fetchAdminData();
    } catch (error) {
      showToast('Failed to update user role', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure? This will also delete all their artworks.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('User deleted successfully', 'success');
      fetchAdminData();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete user', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing': return 'bg-blue-100 text-blue-700';
      case 'shipped': return 'bg-purple-100 text-purple-700';
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'artist': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-2 african-accent inline-block">
            Admin Dashboard
          </h1>
          <p className="text-gray-600">Manage your marketplace</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-lg p-2 mb-8">
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'artworks', label: 'Artworks', icon: '🖼️' },
              { id: 'orders', label: 'Orders', icon: '📦' },
              { id: 'users', label: 'Users', icon: '👥' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-[120px] px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-orange-50'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Overview */}
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
              {[
                { label: 'Total Users', value: stats.totalUsers, icon: '👥', color: 'from-blue-500 to-blue-600' },
                { label: 'Artists', value: stats.totalArtists, icon: '🎨', color: 'from-purple-500 to-purple-600' },
                { label: 'Artworks', value: stats.totalArtworks, icon: '🖼️', color: 'from-orange-500 to-red-600' },
                { label: 'Orders', value: stats.totalOrders, icon: '📦', color: 'from-green-500 to-green-600' },
                { label: 'Revenue', value: `$${stats.totalRevenue.toFixed(2)}`, icon: '💰', color: 'from-yellow-500 to-yellow-600' }
              ].map((stat, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-african transition-shadow duration-300">
                  <div className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center text-2xl mb-4`}>
                    {stat.icon}
                  </div>
                  <p className="text-gray-600 text-sm mb-1">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">Recent Orders</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-orange-100">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Order ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Items</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Total</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.slice(0, 10).map((order) => (
                      <tr key={order._id} className="border-b border-orange-50 hover:bg-orange-50 transition-colors duration-200">
                        <td className="py-3 px-4 text-sm">#{order._id.slice(-8).toUpperCase()}</td>
                        <td className="py-3 px-4 text-sm">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-sm">{order.items?.length} items</td>
                        <td className="py-3 px-4 text-sm font-semibold">${order.totalAmount?.toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.orderStatus)}`}>
                            {order.orderStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {orders.length === 0 && (
                  <p className="text-center py-8 text-gray-500">No orders yet</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Artworks Management */}
        {activeTab === 'artworks' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">All Artworks ({artworks.length})</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {artworks.map((artwork) => (
                <div key={artwork._id} className="border border-orange-100 rounded-xl p-4 hover:shadow-lg transition-shadow duration-300">
                  <img
                    src={artwork.images?.[0] || 'https://placehold.co/200x200/fb923c/ffffff?text=Art'}
                    alt={artwork.title}
                    className="w-full h-48 object-cover rounded-lg mb-3"
                  />
                  <h3 className="font-bold text-gray-900 truncate mb-1">{artwork.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">by {artwork.artist?.name}</p>
                  <p className="text-lg font-bold text-orange-600 mb-3">${artwork.price}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/artwork/${artwork._id}`)}
                      className="flex-1 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDeleteArtwork(artwork._id)}
                      className="flex-1 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {artworks.length === 0 && (
              <p className="text-center py-12 text-gray-500">No artworks yet</p>
            )}
          </div>
        )}

        {/* Orders Management */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">All Orders ({orders.length})</h2>
            <div className="space-y-4">
              {orders.map((order) => (
                <div key={order._id} className="border border-orange-100 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg mb-2">
                        Order #{order._id.slice(-8).toUpperCase()}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {new Date(order.createdAt).toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600">
                        Customer: {order.shippingAddress?.fullName}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-orange-600 mb-2">
                        ${order.totalAmount?.toFixed(2)}
                      </p>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.orderStatus)}`}>
                        {order.orderStatus}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Items:</p>
                    {order.items?.map((item, index) => (
                      <p key={index} className="text-sm text-gray-600">
                        • {item.title} - ${item.price}
                      </p>
                    ))}
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => handleUpdateOrderStatus(order._id, 'processing')}
                      className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600"
                    >
                      Set Processing
                    </button>
                    <button
                      onClick={() => handleUpdateOrderStatus(order._id, 'shipped')}
                      className="px-4 py-2 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600"
                    >
                      Set Shipped
                    </button>
                    <button
                      onClick={() => handleUpdateOrderStatus(order._id, 'delivered')}
                      className="px-4 py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600"
                    >
                      Set Delivered
                    </button>
                    <button
                      onClick={() => handleUpdateOrderStatus(order._id, 'cancelled')}
                      className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600"
                    >
                      Cancel Order
                    </button>
                  </div>
                </div>
              ))}
              {orders.length === 0 && (
                <p className="text-center py-12 text-gray-500">No orders yet</p>
              )}
            </div>
          </div>
        )}

        {/* Users Management */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">All Users ({users.length})</h2>
            {users.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-orange-100">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Role</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Joined</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u._id} className="border-b border-orange-50 hover:bg-orange-50 transition-colors duration-200">
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                              {u.name?.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-gray-900">{u.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{u.email}</td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${getRoleBadge(u.role)}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(u.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          {u._id !== user._id && (
                            <div className="flex gap-2">
                              <select
                                value={u.role}
                                onChange={(e) => handleUpdateUserRole(u._id, e.target.value)}
                                className="text-xs px-2 py-1 border border-orange-200 rounded-lg focus:border-orange-500 outline-none"
                              >
                                <option value="user">User</option>
                                <option value="artist">Artist</option>
                                <option value="admin">Admin</option>
                              </select>
                              <button
                                onClick={() => handleDeleteUser(u._id)}
                                className="px-3 py-1 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                          {u._id === user._id && (
                            <span className="text-xs text-gray-400 italic">You</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-2">User data is loading or unavailable.</p>
                <p className="text-sm text-gray-400">
                  Make sure the admin API endpoint is running.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;